/**
 * DialogBox — accessible modal dialog.
 *
 * Usage:
 *   <DialogBox
 *     open={isOpen}
 *     title="Confirm Action"
 *     onClose={() => setOpen(false)}
 *     actions={[
 *       { label: 'Cancel', variant: 'secondary', onClick: () => setOpen(false) },
 *       { label: 'Confirm', variant: 'primary', onClick: handleConfirm },
 *     ]}
 *   >
 *     Are you sure you want to proceed?
 *   </DialogBox>
 *
 * - Closes on Escape key or overlay click.
 * - Focus is trapped inside the dialog while open.
 * - Unmounts from DOM when `open` is false (keeps animations simple).
 */

import React, { useEffect, useRef } from 'react';
import type { DialogSize, DialogVariant, ActionButton } from '../../types';

export interface DialogBoxProps {
  open: boolean;
  title: string;
  titleIcon?: React.ReactNode;
  /** Optional subtitle / description shown beneath the title */
  subtitle?: string;
  children?: React.ReactNode;
  size?: DialogSize;
  variant?: DialogVariant;
  /** Called when user requests close (Escape, overlay click, or × button) */
  onClose?: () => void;
  /** Footer action buttons (right-aligned) */
  actions?: ActionButton[];
  /** Left-side footer slot (e.g. a "Delete" button) */
  footerLeft?: React.ReactNode;
  /** When true, clicking the overlay does NOT close the dialog */
  disableOverlayClose?: boolean;
  /** Optional class applied to the overlay wrapper */
  overlayClassName?: string;
  className?: string;
}

export function DialogBox({
  open,
  title,
  titleIcon,
  subtitle,
  children,
  size = 'md',
  variant = 'default',
  onClose,
  actions = [],
  footerLeft,
  disableOverlayClose = false,
  overlayClassName = '',
  className = '',
}: DialogBoxProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  // ── Escape key ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  // ── Focus trap ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open || !dialogRef.current) return;
    const dialogEl = dialogRef.current;

    const focusable = dialogEl.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    const trap = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (!first || !last) {
        e.preventDefault();
        dialogEl.focus();
        return;
      }

      if (e.shiftKey) {
        if (document.activeElement === first || document.activeElement === dialogEl) {
          e.preventDefault();
          last?.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };

    document.addEventListener('keydown', trap);
    dialogEl.focus();
    return () => document.removeEventListener('keydown', trap);
  }, [open]);

  // ── Scroll lock ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!open) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disableOverlayClose) return;
    if (e.target === e.currentTarget) onClose?.();
  };

  return (
    <div
      className={`kci-dialog-overlay ${overlayClassName}`.trim()}
      role="presentation"
      aria-modal="true"
      onClick={handleOverlayClick}
    >
      <div
        ref={dialogRef}
        className={`kci-dialog kci-dialog--${size} kci-dialog--${variant} ${className}`.trim()}
        role="dialog"
        tabIndex={-1}
        aria-labelledby="kci-dialog-title"
        aria-describedby={subtitle ? 'kci-dialog-subtitle' : undefined}
      >
        {/* Header */}
        <div className="kci-dialog__header">
          <div className="kci-dialog__header-content">
            <h2 id="kci-dialog-title" className="kci-dialog__title">
              {titleIcon && (
                <span className="kci-dialog__title-icon" aria-hidden="true">
                  {titleIcon}
                </span>
              )}
              {title}
            </h2>
            {subtitle && (
              <p id="kci-dialog-subtitle" className="kci-dialog__subtitle">
                {subtitle}
              </p>
            )}
          </div>
          {onClose && (
            <button
              type="button"
              className="kci-dialog__close"
              aria-label="Close dialog"
              onClick={onClose}
            >
              ✕
            </button>
          )}
        </div>

        {/* Body */}
        {children && <div className="kci-dialog__body">{children}</div>}

        {/* Footer */}
        {(actions.length > 0 || footerLeft) && (
          <div
            className={`kci-dialog__footer${footerLeft ? ' kci-dialog__footer--space-between' : ''}`}
          >
            {footerLeft && <div>{footerLeft}</div>}
            <div style={{ display: 'flex', gap: '8px' }}>
              {actions.map((action, i) => (
                <button
                  key={i}
                  type="button"
                  className={`kci-btn kci-btn--${action.variant ?? 'primary'}`}
                  disabled={action.disabled}
                  onClick={action.onClick}
                >
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
